import { useState, useEffect } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FriendStable from "./FriendStable";
import TodayGoal from "./TodayGoal";
import PetLevelBar from "./PetLevelBar";
import QuickHeal from "./QuickHeal";
import { usePet, MOOD_COPY, TIER_COLORS, formatRebirthCost } from "./usePet";
import AetherAshPetBlocks from "./AetherAshPetBlocks";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhoenixPetPage() {
  const { data, loading, reload } = usePet();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [feeding, setFeeding] = useState(null);
  const [reborning, setReborning] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (data?.greeter_quote) {
      // Only show the greeter once per session
      const key = `omni:pet-greeter-${new Date().toISOString().slice(0, 10)}`;
      if (!sessionStorage.getItem(key)) {
        toast(data.greeter_quote, { duration: 5500 });
        sessionStorage.setItem(key, "1");
      }
    }
  }, [data?.greeter_quote]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 pt-28 pb-20 text-center text-neutral-500">Loading…</main>
        <Footer />
      </div>
    );
  }

  if (!data?.pet) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 pt-28 pb-20">
          {/* ⚡ Oct 2026 — Aether & Ash stays visible even when the user has
              no pet yet (post-evolution, mid-rebirth, first-login). Roc's
              bug: AA used to disappear here because the no-pet branch bailed
              before this component rendered. */}
          <AetherAshPetBlocks />
          <div className="text-center mt-8">
            <div className="text-6xl mb-3">🦅</div>
            <div className="font-display text-3xl mb-2">Sign in to meet your phoenix</div>
            <div className="text-neutral-400 text-sm">Every account gets a free Phoenix Pet.</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pet = data.pet;
  const mood = MOOD_COPY[pet.mood] || MOOD_COPY.happy;
  const tierStyle = TIER_COLORS[pet.tier] || TIER_COLORS.egg;
  const xpPct = Math.min(100, (pet.xp / Math.max(1, pet.xp_cap)) * 100);
  const agePct = Math.min(100, (pet.age_days / Math.max(0.1, pet.max_age_days)) * 100);

  const feed = async (key) => {
    setFeeding(key);
    try {
      const { data: res } = await axios.post(`${API}/pet/feed`, { item_key: key });
      const item = data.feed_items_catalog[key];
      toast.success(
        res.hungry_penalty_applied
          ? `Fed ${item.name} → +${res.xp_gained} XP (50% — pet was hungry)`
          : `Fed ${item.name} → +${res.xp_gained} XP 🎉`
      );
      if (res.died) toast.error(`💀 ${pet.name} burned out! Perform the Rebirth Ritual below.`, { duration: 8000 });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't feed.");
    } finally {
      setFeeding(null);
    }
  };

  const feedAll = async () => {
    setFeeding("__ALL__");
    try {
      const { data: res } = await axios.post(`${API}/pet/feed-all`, {});
      const lines = (res.fed || []).map((f) => `${f.icon} ${f.qty_used}× ${f.name} (+${f.total_xp} XP)`).join(" · ");
      toast.success(`🍽️ Used all — +${res.total_xp} XP total · ${lines}`, { duration: 6500 });
      if (res.died) toast.error(`💀 ${pet.name} burned out! Perform the Rebirth Ritual below.`, { duration: 8000 });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't bulk feed.");
    } finally {
      setFeeding(null);
    }
  };

  const rebirth = async () => {
    setReborning(true);
    try {
      const { data: res } = await axios.post(`${API}/pet/rebirth`);
      toast.success(`🐦‍🔥 ${pet.name} reborn as ${res.new_tier_label}!`, { duration: 8000 });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebirth failed.");
    } finally {
      setReborning(false);
    }
  };

  const doRename = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/pet/rename`, { name });
      toast.success(`Renamed to ${name}`);
      setRenaming(false);
      setNewName("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename.");
    }
  };

  // 🛠️🥚 Owner-only pet reset — wipes pet back to a fresh white Aurora
  // Phoenix Egg (Tier 1, Level 1, XP 0, empty rebirth+level history) and
  // closes stale in-flight arena fights so pre-reset HP snapshots stop
  // bleeding through. Non-owners never see the button (hard-gated via
  // `user?.role === "owner"`). Confirmed via `window.confirm` because
  // this is destructive.
  const resetPet = async () => {
    if (!window.confirm("🥚 Reset your pet to a fresh white Aurora Phoenix Egg? This wipes level, XP, rebirth history, and training stats. Cannot be undone.")) return;
    setResetting(true);
    try {
      const { data: res } = await axios.post(`${API}/dev/owner/reset-pet`);
      toast.success(res?.message || "🥚 Pet reset to a fresh egg.", { duration: 5500 });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pet-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">Your Companion</div>
          <h1 className="font-display text-4xl sm:text-6xl mt-2">🦅 Phoenix Pet</h1>
        </div>

        {/* Hero card */}
        <div className={`bg-[#0F0F0F] border-2 ${tierStyle.ring} ${tierStyle.glow} p-6 sm:p-8 text-center mb-6`}>
          <div className={`mx-auto w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-[#0A0A0A] border-2 ${tierStyle.ring} flex items-center justify-center mb-4`}>
            <span className="text-7xl sm:text-8xl leading-none">{pet.alive ? pet.icon : "💀"}</span>
          </div>
          {!renaming ? (
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="font-display text-3xl">{pet.name}</div>
              <button onClick={() => { setNewName(pet.name); setRenaming(true); }}
                      data-testid="pet-rename-trigger"
                      className="text-xs text-neutral-500 hover:text-[#D4AF37] uppercase tracking-widest">edit</button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 mb-2 max-w-xs mx-auto">
              <input
                data-testid="pet-rename-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={20}
                className="flex-1 bg-[#0A0A0A] border-2 border-zinc-700 focus:border-[#D4AF37] px-3 py-1 text-white outline-none text-center"
                onKeyDown={(e) => { if (e.key === "Enter") doRename(); if (e.key === "Escape") setRenaming(false); }}
                autoFocus
              />
              <button data-testid="pet-rename-save" onClick={doRename} className="px-3 py-1 bg-[#D4AF37] text-black font-bold text-xs uppercase">Save</button>
              <button onClick={() => setRenaming(false)} className="text-neutral-500 text-xs">×</button>
            </div>
          )}
          <div className="text-sm text-neutral-300 mb-1">{pet.tier_label}</div>
          <div className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-zinc-900" title={mood.hint}>
            <span>{mood.face}</span>
            <span className="uppercase tracking-widest text-neutral-400">{mood.label}</span>
          </div>

          {/* 🛠️🥚 Owner-only reset button — hard-gated, subtle, one-tap.
              Wipes to a fresh Aurora Phoenix Egg. Non-owners never see this. */}
          {isOwner && (
            <div className="mt-3">
              <button
                data-testid="owner-reset-pet-btn"
                onClick={resetPet}
                disabled={resetting}
                className="text-[10px] uppercase tracking-[0.3em] px-3 py-1 border border-zinc-700 text-neutral-500 hover:border-[#D4AF37] hover:text-[#D4AF37] transition disabled:opacity-40"
                title="Owner-only: reset pet to a fresh white egg"
              >
                {resetting ? "🥚 resetting…" : "🥚 reset my pet"}
              </button>
            </div>
          )}

          {/* XP bar */}
          <div className="mt-5 max-w-md mx-auto">
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              <span>XP</span>
              <span className="tabular-nums">{pet.xp} / {pet.xp_cap}</span>
            </div>
            <div className="w-full bg-zinc-900 h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 via-red-500 to-pink-500" style={{ width: `${xpPct}%` }} />
            </div>
            {/* Age bar removed — clashed with the theme. Age still drives ascension under the hood. */}
          </div>

          {!pet.alive ? (
            <RebirthCard pet={pet} onRebirth={rebirth} busy={reborning} />
          ) : (
            <div className="mt-5 text-xs text-neutral-500">
              {pet.next_tier && pet.next_tier !== pet.tier && (
                <>Next tier: <span className="text-white">{pet.next_tier_icon} {pet.next_tier_label}</span> — fills XP cap OR ages out for ascension.</>
              )}
            </div>
          )}
        </div>

        {/* Feed grid */}
        {pet.alive && (
          <div className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="font-display text-2xl">🍽️ Feed</h2>
              {(() => {
                const totalItems = Object.keys(data.feed_items_catalog).reduce(
                  (n, k) => n + (data.inventory?.[k] || 0), 0
                );
                const totalXp = Object.entries(data.feed_items_catalog).reduce(
                  (n, [k, item]) => n + (data.inventory?.[k] || 0) * item.xp, 0
                );
                return totalItems > 0 ? (
                  <button
                    data-testid="pet-feed-use-all"
                    onClick={feedAll}
                    disabled={feeding !== null}
                    className={`px-3 py-2 rounded text-xs font-bold uppercase tracking-wider ${
                      feeding !== null
                        ? "bg-zinc-900 text-neutral-600 cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-500 text-white"
                    }`}
                    title={`Use all ${totalItems} XP item${totalItems === 1 ? "" : "s"} for +${totalXp} XP`}
                  >
                    {feeding === "__ALL__" ? "Feeding…" : `🍔 Use All (+${totalXp} XP)`}
                  </button>
                ) : null;
              })()}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(data.feed_items_catalog).map(([key, item]) => {
                const count = data.inventory?.[key] || 0;
                const disabled = count <= 0 || feeding === key;
                return (
                  <div key={key} className="bg-[#0F0F0F] border-2 border-zinc-900 p-4 flex flex-col items-center text-center">
                    <span className="text-4xl mb-1">{item.icon}</span>
                    <div className="font-bold text-sm">{item.name}</div>
                    <div className="text-[10px] text-neutral-500 italic">{item.tagline}</div>
                    <div className="text-amber-400 font-bold text-sm mt-1">+{item.xp} XP</div>
                    <div className="text-[10px] text-neutral-500 mt-1">You have: <span className="tabular-nums">{count}</span></div>
                    <button
                      data-testid={`pet-feed-${key}`}
                      onClick={() => feed(key)}
                      disabled={disabled}
                      className={`mt-3 w-full py-2 font-bold uppercase tracking-widest text-xs ${
                        disabled
                          ? "bg-zinc-900 text-neutral-600 cursor-not-allowed"
                          : "bg-[#D4AF37] hover:bg-yellow-400 text-black"
                      }`}
                    >
                      {count <= 0 ? "Out of stock" : "Feed"}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-neutral-500 mt-3 text-center">
              Earn feed items from egg hatches, the cauldron, the shop, and daily activity rewards.
            </div>
          </div>
        )}

        {/* 🛡️ Armor slot teaser — Phase 2 preview */}
        <div className="mb-6" data-testid="pet-armor-slots">
          <h2 className="font-display text-2xl mb-1">🛡️ Battle Armor Slots</h2>
          <div className="text-xs text-neutral-500 mb-3">
            Each pet evolution unlocks a new armor slot. Hatch Armor Eggs in the upcoming Arena Store to equip them.
          </div>
          <ArmorSlotsRow currentTier={pet.tier} />
        </div>

        {/* 🎯 Today's Goal — auto-picked daily target with 🍚/🪙 reward */}
        <TodayGoal />
        <PetLevelBar />

        {/* 🔥 Quick-Heal — Phoenix Flint / Aglare Quill / Ignis Core */}
        <QuickHeal pet={pet} hpItems={data.hp_items} onHealed={reload} />

        {/* 🐦 Friend Stable — Feed-A-Friend daily (+20 XP per friend) */}
        <FriendStable />

        {/* ⚡ Aether & Ash — Collection Stats, Collection Breadth,
            How Combat Power Is Built, and The Ladder — moved here from
            the Aether & Ash page per owner directive Oct 2026. */}
        <AetherAshPetBlocks />

        {/* Tier ladder reference */}
        <div className="mb-6">
          <h2 className="font-display text-2xl mb-3">🪶 Lifecycle</h2>
          <div className="grid grid-cols-5 gap-2">
            {["egg", "chick", "fledgling", "phoenix", "inferno_phoenix"].map((t) => {
              const tcfg = data.tiers_catalog[t];
              const current = t === pet.tier;
              return (
                <div
                  key={t}
                  className={`p-3 border-2 ${current ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-zinc-900"} text-center`}
                >
                  <div className="text-3xl">{tcfg.icon}</div>
                  <div className="text-[9px] uppercase tracking-widest mt-1 text-neutral-400">{tcfg.name}</div>
                  <div className="text-[9px] text-neutral-600 tabular-nums">{tcfg.xp_cap} XP · {tcfg.max_age_days}d</div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-neutral-500 mt-3 text-center italic">
            Pet grows egg → chick → fledgling → phoenix → <span className="text-yellow-300 font-semibold">full phoenix</span>.
            Only at full phoenix does hitting the XP cap trigger Ash. Rebirth then hatches a fresh
            <span className="text-white font-semibold"> white egg</span> of the next tier
            (Aurora → Inferno → Ember Dawn → …), scaling its adult cap 1.5× higher. 2% training stats carry forward.
            Bond, name, glimmers, and skin are preserved.
          </div>
        </div>

        {/* Phase 2 teaser */}
        <div className="bg-gradient-to-r from-purple-900/30 via-red-900/30 to-amber-900/30 border-2 border-[#D4AF37]/30 p-5 text-center" data-testid="pet-arena-teaser">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">Phase 2 · LIVE NOW</div>
          <div className="font-display text-xl mb-2">⚔️ Phoenix Battles — The Arena Is Open</div>
          <div className="text-xs text-neutral-400 max-w-md mx-auto leading-relaxed mb-3">
            Spar with CPU dummies or raid Greyfang, Stormwing, and The Unrisen. Win Battle Coins, feed drops, and build a streak multiplier up to <span className="text-[#D4AF37] font-bold">5x</span>.
          </div>
          <a
            href="/arena"
            data-testid="pet-arena-cta"
            className="inline-block bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-xs py-2 px-5"
          >
            Enter The Arena →
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function RebirthCard({ pet, onRebirth, busy }) {
  const cost = formatRebirthCost(pet.rebirth_cost);
  const readyAt = pet.rebirth_ready_at ? new Date(pet.rebirth_ready_at).getTime() : 0;
  const nowMs = Date.now();
  const waitingMs = Math.max(0, readyAt - nowMs);
  const canRebirth = waitingMs <= 0;
  const tierNumber = Number.parseInt(pet.tier_number) || 1;
  const nextTierNumber = tierNumber + 1;
  // Adult color preview (the new egg will be white; this is what it grows INTO)
  const nextSpectrum = pet.next_spectrum || {};
  return (
    <div className="mt-6 bg-red-500/10 border border-[#DC2626] p-5 text-center">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#DC2626] font-bold mb-2">💀 In Ash Form</div>
      <div className="font-display text-2xl mb-1">Rebirth Ritual</div>

      {/* 🌈 Tier-Advance Preview — reborn as fresh white egg → grows into next adult */}
      <div className="my-3 p-3 bg-black/40 border border-white/10" style={{ borderRadius: 6 }}>
        <div className="flex items-center justify-center gap-3 text-3xl">
          <div className="flex flex-col items-center">
            <span>{pet.icon}</span>
            <span className="text-[9px] uppercase tracking-widest text-white/40 mt-1">Tier {tierNumber}</span>
            <span className="text-[9px] text-white/50">{pet.spectrum_label || pet.tier_label || "Aurora"}</span>
          </div>
          <span className="text-yellow-400 text-xl">→</span>
          <div className="flex flex-col items-center">
            <span>🥚</span>
            <span className="text-[9px] uppercase tracking-widest text-yellow-300 mt-1 font-bold">Tier {nextTierNumber}</span>
            <span className="text-[9px] text-white/70">
              {nextSpectrum.codename || "Inferno"} <span className="opacity-60">(egg → adult)</span>
            </span>
          </div>
        </div>
        <div className="text-[10px] text-white/50 mt-2 leading-snug">
          New form: <span className="text-white font-semibold">🥚 White Egg</span> · grows through{" "}
          <span className="opacity-80">chick → fledgling → phoenix →</span>{" "}
          <span className="text-yellow-300 font-bold">{nextSpectrum.adult_label || nextSpectrum.codename || "Full Phoenix"}</span>
          <span className="opacity-70"> (adult cap {(nextSpectrum.xp_cap || 0).toLocaleString()} XP)</span>
        </div>
        <div className="text-[10px] text-white/50 mt-1">
          Carry-over: <span className="text-emerald-400 font-bold">2% training stats</span> · Bond, name, glimmers, skin preserved
        </div>
      </div>

      <div className="text-sm text-amber-400 font-bold tabular-nums mb-3">Cost: {cost}</div>
      {pet.is_premium && (
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">⚡ Premium: 50% off + skip wait</div>
      )}
      <button
        data-testid="pet-rebirth-btn"
        onClick={onRebirth}
        disabled={!canRebirth || busy}
        className={`px-6 py-2 font-bold uppercase tracking-widest text-xs ${
          canRebirth && !busy
            ? "bg-[#D4AF37] hover:bg-yellow-400 text-black"
            : "bg-zinc-800 text-neutral-500 cursor-not-allowed"
        }`}
      >
        {busy ? "Reviving…" : canRebirth ? "Perform Ritual 🔥" : `Cool down: ${Math.ceil(waitingMs / 1000 / 3600)}h`}
      </button>
    </div>
  );
}


// 🛡️ Armor slots gated by pet evolution — Phase 2 teaser
const ARMOR_SLOTS = [
  { id: "helmet", icon: "🪖", label: "Helmet", unlocked_at: "chick",   unlocked_at_label: "Chick" },
  { id: "chest",  icon: "🛡️", label: "Chest",  unlocked_at: "fledgling", unlocked_at_label: "Fledgling" },
  { id: "wings",  icon: "🦅", label: "Wings",  unlocked_at: "phoenix", unlocked_at_label: "Phoenix" },
  { id: "talon",  icon: "🦶", label: "Talon",  unlocked_at: "inferno_phoenix", unlocked_at_label: "Inferno" },
  { id: "aura",   icon: "✨", label: "Aura",   unlocked_at: "prestige", unlocked_at_label: "Prestige" },
];
const TIER_ORDER = ["egg", "chick", "fledgling", "phoenix", "inferno_phoenix", "prestige"];

function ArmorSlotsRow({ currentTier }) {
  const currentIdx = TIER_ORDER.indexOf(currentTier);
  return (
    <div className="grid grid-cols-5 gap-2">
      {ARMOR_SLOTS.map((slot) => {
        const requiredIdx = TIER_ORDER.indexOf(slot.unlocked_at);
        const unlocked = currentIdx >= requiredIdx;
        return (
          <div
            key={slot.id}
            data-testid={`armor-slot-${slot.id}`}
            className={`p-3 border-2 text-center relative overflow-hidden ${
              unlocked
                ? "border-[#D4AF37] bg-[#D4AF37]/10"
                : "border-zinc-900 bg-[#0A0A0A]"
            }`}
          >
            <div className={`text-3xl ${unlocked ? "" : "grayscale opacity-40 blur-[1px]"}`}>
              {unlocked ? slot.icon : "🔒"}
            </div>
            <div className={`text-[9px] uppercase tracking-widest mt-1 font-bold ${
              unlocked ? "text-[#D4AF37]" : "text-neutral-500"
            }`}>
              {slot.label}
            </div>
            <div className="text-[8px] text-neutral-600 mt-0.5">
              {unlocked ? (
                <span className="text-amber-400">Equip soon</span>
              ) : (
                <>Reach {slot.unlocked_at_label}</>
              )}
            </div>
            {unlocked && (
              <div className="absolute top-1 right-1 text-[8px] text-[#D4AF37] font-bold">🔓</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
